import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Gavel, X, Vote, Edit, Check, CheckCircle2 } from "lucide-react";

interface MotionsSectionProps {
  data: {
    motions: any[];
    decisions: any[];
  };
  onChange: (updates: any) => void;
  isFirstMeeting?: boolean;
}

export function MotionsSection({ data, onChange, isFirstMeeting }: MotionsSectionProps) {
  const [newMotion, setNewMotion] = useState({
    motion_text: "",
    made_by: "",
    seconded_by: "",
    discussion_summary: "",
    vote_result: "",
    votes_for: "",
    votes_against: "",
    abstentions: "",
  });

  // Editing state for existing motions
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editMotion, setEditMotion] = useState({
    made_by: "",
    seconded_by: "",
    discussion_summary: "",
    vote_result: "",
    votes_for: "",
    votes_against: "",
    abstentions: "",
  });

  const addMotion = () => {
    if (!newMotion.motion_text.trim()) return;

    onChange({
      motions: [...data.motions, {
        ...newMotion,
        timestamp: new Date().toISOString(),
        votes_for: parseInt(newMotion.votes_for) || 0,
        votes_against: parseInt(newMotion.votes_against) || 0,
        abstentions: parseInt(newMotion.abstentions) || 0,
      }],
    });

    setNewMotion({
      motion_text: "",
      made_by: "",
      seconded_by: "",
      discussion_summary: "",
      vote_result: "",
      votes_for: "",
      votes_against: "",
      abstentions: "",
    });
  };

  const removeMotion = (index: number) => {
    onChange({
      motions: data.motions.filter((_, i) => i !== index),
    });
    // Clear editing if we're removing the motion being edited
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const startEditing = (index: number) => {
    const motion = data.motions[index];
    setEditMotion({
      made_by: motion.made_by || "",
      seconded_by: motion.seconded_by || "",
      discussion_summary: motion.discussion_summary || "",
      vote_result: motion.vote_result || "",
      votes_for: String(motion.votes_for || ""),
      votes_against: String(motion.votes_against || ""),
      abstentions: String(motion.abstentions || ""),
    });
    setEditingIndex(index);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
  };

  const updateMotion = (index: number) => {
    const updatedMotions = [...data.motions];
    updatedMotions[index] = {
      ...updatedMotions[index],
      made_by: editMotion.made_by,
      seconded_by: editMotion.seconded_by,
      discussion_summary: editMotion.discussion_summary,
      vote_result: editMotion.vote_result,
      votes_for: parseInt(editMotion.votes_for) || 0,
      votes_against: parseInt(editMotion.votes_against) || 0,
      abstentions: parseInt(editMotion.abstentions) || 0,
    };
    onChange({ motions: updatedMotions });
    setEditingIndex(null);
  };

  const getResultColor = (result: string) => {
    switch (result) {
      case "passed": return "default";
      case "failed": return "destructive";
      case "tabled": return "secondary";
      case "withdrawn": return "outline";
      default: return "outline";
    }
  };

  const getResultBorderClass = (result: string) => {
    switch (result) {
      case "passed": return "border-green-300 bg-green-50/30";
      case "failed": return "border-red-200 bg-red-50/30";
      case "tabled": return "border-slate-200 bg-slate-50/30";
      default: return "";
    }
  };

  const hasSuggestedMotions = data.motions?.some((m: any) => m.is_suggested);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-4">Motions & Votes</h2>
        <p className="text-muted-foreground">
          Record motions, seconds, discussions, and voting results.
        </p>
      </div>

      {/* First Meeting Helper Banner */}
      {isFirstMeeting && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="font-medium text-green-800">
                ✓ All Resolutions Already Passed!
              </p>
              <p className="text-sm text-green-700">
                Great news! All your required resolutions have been pre-filled with voting details. 
                As the sole board member at this organizational meeting, you've already voted "Yes" on each one.
                Everything is marked as "Passed" - just review the list below and proceed to the next section!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pre-filled Motions Notice (for non-first meetings) */}
      {!isFirstMeeting && hasSuggestedMotions && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <strong>Pre-filled Resolutions:</strong> The motions below were pre-populated from your journey step suggestions. 
          Complete the voting details for each, or remove any that don't apply.
        </div>
      )}

      {/* Add New Motion */}
      <Card className="p-4 space-y-4">
        <h3 className="font-semibold flex items-center gap-2">
          <Gavel className="h-5 w-5" />
          Record New Motion
        </h3>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="motion-text">Motion Text *</Label>
            <Textarea
              id="motion-text"
              value={newMotion.motion_text}
              onChange={(e) => setNewMotion({ ...newMotion, motion_text: e.target.value })}
              placeholder="Enter the exact wording of the motion..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="made-by">Made By</Label>
              <Input
                id="made-by"
                value={newMotion.made_by}
                onChange={(e) => setNewMotion({ ...newMotion, made_by: e.target.value })}
                placeholder="Member who made the motion"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="seconded-by">Seconded By</Label>
              <Input
                id="seconded-by"
                value={newMotion.seconded_by}
                onChange={(e) => setNewMotion({ ...newMotion, seconded_by: e.target.value })}
                placeholder="Member who seconded"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="discussion">Discussion Summary</Label>
            <Textarea
              id="discussion"
              value={newMotion.discussion_summary}
              onChange={(e) => setNewMotion({ ...newMotion, discussion_summary: e.target.value })}
              placeholder="Key points from the discussion..."
              rows={3}
            />
          </div>

          <div className="space-y-3">
            <Label>Vote Result</Label>
            <RadioGroup
              value={newMotion.vote_result}
              onValueChange={(value) => setNewMotion({ ...newMotion, vote_result: value })}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="passed" id="passed" />
                <Label htmlFor="passed" className="cursor-pointer">Passed</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="failed" id="failed" />
                <Label htmlFor="failed" className="cursor-pointer">Failed</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="tabled" id="tabled" />
                <Label htmlFor="tabled" className="cursor-pointer">Tabled</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="withdrawn" id="withdrawn" />
                <Label htmlFor="withdrawn" className="cursor-pointer">Withdrawn</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="votes-for">Votes For</Label>
              <Input
                id="votes-for"
                type="number"
                min="0"
                value={newMotion.votes_for}
                onChange={(e) => setNewMotion({ ...newMotion, votes_for: e.target.value })}
                placeholder="0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="votes-against">Votes Against</Label>
              <Input
                id="votes-against"
                type="number"
                min="0"
                value={newMotion.votes_against}
                onChange={(e) => setNewMotion({ ...newMotion, votes_against: e.target.value })}
                placeholder="0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="abstentions">Abstentions</Label>
              <Input
                id="abstentions"
                type="number"
                min="0"
                value={newMotion.abstentions}
                onChange={(e) => setNewMotion({ ...newMotion, abstentions: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>
        </div>

        <Button onClick={addMotion} disabled={!newMotion.motion_text.trim()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Motion
        </Button>
      </Card>

      {/* Motions List */}
      {data.motions.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Recorded Motions ({data.motions.length})</h3>

          {data.motions.map((motion: any, index: number) => {
            const isEditing = editingIndex === index;
            const isPending = !motion.vote_result;
            const baseClass = motion.is_suggested && isPending 
              ? 'border-amber-200 bg-amber-50/30' 
              : getResultBorderClass(motion.vote_result);

            return (
              <Card key={index} className={`p-4 ${baseClass}`}>
                {isEditing ? (
                  // Editing Mode
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm text-muted-foreground">Recording Vote for:</h4>
                      <Button variant="ghost" size="sm" onClick={cancelEditing}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="font-medium border-l-2 border-primary pl-3">{motion.motion_text}</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Made By</Label>
                        <Input
                          value={editMotion.made_by}
                          onChange={(e) => setEditMotion({ ...editMotion, made_by: e.target.value })}
                          placeholder="Member who made the motion"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Seconded By</Label>
                        <Input
                          value={editMotion.seconded_by}
                          onChange={(e) => setEditMotion({ ...editMotion, seconded_by: e.target.value })}
                          placeholder="Member who seconded"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Discussion Summary</Label>
                      <Textarea
                        value={editMotion.discussion_summary}
                        onChange={(e) => setEditMotion({ ...editMotion, discussion_summary: e.target.value })}
                        placeholder="Key points from the discussion..."
                        rows={2}
                      />
                    </div>

                    <div className="space-y-3">
                      <Label>Vote Result *</Label>
                      <RadioGroup
                        value={editMotion.vote_result}
                        onValueChange={(value) => setEditMotion({ ...editMotion, vote_result: value })}
                        className="flex flex-wrap gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="passed" id={`edit-passed-${index}`} />
                          <Label htmlFor={`edit-passed-${index}`} className="cursor-pointer text-green-700">Passed</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="failed" id={`edit-failed-${index}`} />
                          <Label htmlFor={`edit-failed-${index}`} className="cursor-pointer text-red-700">Failed</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="tabled" id={`edit-tabled-${index}`} />
                          <Label htmlFor={`edit-tabled-${index}`} className="cursor-pointer">Tabled</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="withdrawn" id={`edit-withdrawn-${index}`} />
                          <Label htmlFor={`edit-withdrawn-${index}`} className="cursor-pointer">Withdrawn</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Votes For</Label>
                        <Input
                          type="number"
                          min="0"
                          value={editMotion.votes_for}
                          onChange={(e) => setEditMotion({ ...editMotion, votes_for: e.target.value })}
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Against</Label>
                        <Input
                          type="number"
                          min="0"
                          value={editMotion.votes_against}
                          onChange={(e) => setEditMotion({ ...editMotion, votes_against: e.target.value })}
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Abstentions</Label>
                        <Input
                          type="number"
                          min="0"
                          value={editMotion.abstentions}
                          onChange={(e) => setEditMotion({ ...editMotion, abstentions: e.target.value })}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button onClick={() => updateMotion(index)} disabled={!editMotion.vote_result}>
                        <Check className="h-4 w-4 mr-2" />
                        Save Vote
                      </Button>
                      <Button variant="outline" onClick={cancelEditing}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Display Mode
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-start gap-3 flex-wrap">
                        <Badge 
                          variant={getResultColor(motion.vote_result)}
                          className={motion.vote_result === 'passed' ? 'bg-green-600 hover:bg-green-700' : ''}
                        >
                          {motion.vote_result ? motion.vote_result.charAt(0).toUpperCase() + motion.vote_result.slice(1) : "Pending"}
                        </Badge>
                        {motion.is_suggested && (
                          <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                            {motion.is_required ? 'Required Resolution' : 'Suggested Resolution'}
                          </Badge>
                        )}
                        <div className="flex-1 basis-full sm:basis-auto">
                          <p className="font-medium">{motion.motion_text}</p>
                          {motion.description && (
                            <p className="text-sm text-muted-foreground mt-1">{motion.description}</p>
                          )}
                          <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                            {motion.made_by && <span>Made by: {motion.made_by}</span>}
                            {motion.seconded_by && <span>Seconded by: {motion.seconded_by}</span>}
                          </div>
                        </div>
                      </div>

                      {motion.discussion_summary && (
                        <div className="text-sm">
                          <span className="font-medium">Discussion: </span>
                          <span className="text-muted-foreground">{motion.discussion_summary}</span>
                        </div>
                      )}

                      {motion.vote_result && (
                        <div className="flex gap-6 text-sm">
                          <span className="text-green-600">For: {motion.votes_for || 0}</span>
                          <span className="text-red-600">Against: {motion.votes_against || 0}</span>
                          <span className="text-muted-foreground">Abstained: {motion.abstentions || 0}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1">
                      {/* Record Vote / Edit Vote Button */}
                      {isPending ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEditing(index)}
                          className="text-amber-700 border-amber-300 hover:bg-amber-50"
                        >
                          <Vote className="h-4 w-4 mr-2" />
                          Record Vote
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEditing(index)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      )}

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeMotion(index)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
